/**
 * DealCard — Shared Deal Sourcing Card
 *
 * Authority: WI-SHARED-DEAL-CARD-001 P2.
 *
 * Single canonical render for Tracxn deal cards across both surfaces:
 *  - surface="inbox"  → Work Inbox detail renderer (flat header, View Deck visible)
 *  - surface="agents" → Deal Sourcing agent page row (collapsible header, View Deck ALSO visible)
 *
 * Stale-doc correction (WI-TRACXN-ENRICHMENT-BUILD-LEGS-3-7-001 Leg 5, drive-by): this line
 * previously said "View Deck hidden" on surface="agents". That has been untrue since
 * WI-INVARIANT-UI-SURFACE-REPAIR-001 deliberately made View Deck render on BOTH surfaces
 * (see the render-site comment below, which is the authority). The stale docstring and the
 * matching stale assertion in __tests__/DealCard.test.jsx were corrected together rather than
 * left contradicting the shipped behaviour.
 *
 * Pure display + callback contract. The component does NOT call passDeal /
 * contactDeal directly; instead the parent adapter passes onPass / onContacted
 * callbacks. This keeps the component testable in isolation and lets the
 * adapter own optimistic-removal / refresh / toast policies per surface.
 *
 * RA status — three explicit branches per the WI plan:
 *   1. researcher_synthesis non-empty → render synthesis
 *   2. synthesis null AND ra_completed_at set → "Research ran — no public coverage found."
 *   3. ra_completed_at null → "Research pending."
 *
 * Founders empty + founder_name_from_research set → render the RA-sourced
 * name with a small "from public research — email not yet confirmed" caption.
 *
 * Leg 5 (WI-TRACXN-ENRICHMENT-BUILD-LEGS-3-7-001, ruled render / seat-test
 * redteam): canonical sector_l3 supersedes sector_hint in the header chip row
 * (_resolveSectorDisplay); enrichment_resolution_state renders an explicit
 * "Tracxn has nothing" vs "needs a human look" chip — the latter NEVER reads
 * as "new territory" (_EnrichmentStatusChip); a "Where We Stand" section
 * renders only when sector_l3 is known and admits honestly that no live view
 * read is wired this leg, rather than fabricating a stance (_StanceSection).
 * Pass / Contacted / View Deck outcomes — success AND failure — are announced
 * through a local polite aria-live region (+ optional onAnnounce relay to a
 * parent-owned persistent region), extending the #950/#954 pattern from
 * LinkedInPostAgent.jsx to this card.
 *
 * Tokens: DS_COLORS / DS_TYPOGRAPHY / DS_SPACING from ui-invariants.
 * Labels: AGENT_CONFIG.deal_sourcing.labels (DS_LABELS).
 * Pass dropdown items: AGENT_CONFIG.deal_sourcing.actions[id=pass].dropdown.items.
 * No hardcoded copy. No hardcoded hex.
 */

import React, { useCallback, useState } from 'react';
import {
    EnvelopeSimple,
    XCircle,
    Eye,
    LinkedinLogo,
    User,
    Copy,
    Check,
    CaretDown,
} from '@phosphor-icons/react';

import { Button } from '@/components/ui/button';
import {
    DropdownMenu,
    DropdownMenuContent,
    DropdownMenuItem,
    DropdownMenuTrigger,
} from '@/components/ui/dropdown-menu';
import { FounderDetailPanel } from '@/components/shared/FounderDetailPanel';
import { AGENT_CONFIG, INTENT_TO_BUTTON } from '@/config/task-display-config';
import { DS_COLORS, DS_TYPOGRAPHY, DS_SPACING } from '@/config/ui-invariants';
import useAnnouncer from '@/hooks/useAnnouncer';
import { formatRaise } from '@/lib/formatRaise';
import { buildMailtoHref } from '@/lib/mailtoBuilder';

const DEAL_SOURCING = AGENT_CONFIG.deal_sourcing || {};
const DS_LABELS = DEAL_SOURCING.labels || {};
const PASS_ACTION =
    (DEAL_SOURCING.actions || []).find((a) => a.id === 'pass') || {};
const VIEW_DECK_ACTION =
    (DEAL_SOURCING.actions || []).find((a) => a.id === 'view_deck') || {};
const CONTACTED_ACTION =
    (DEAL_SOURCING.actions || []).find((a) => a.id === 'contacted') || {};
const PASS_REASONS = (PASS_ACTION.dropdown && PASS_ACTION.dropdown.items) || [];

const DEAL_CONTACTABLE_STATUS = 'pending_review';

// Status → contacted-button label. Only `pending_review` enables the button;
// every other live status renders its own non-collapsing label so the CEO
// can read the deal's actual state at a glance instead of a single
// "Pending Enrichment" fallback that hides four other states.
function _statusLabel(status, labels) {
    if (status === 'pending_enrichment') return labels.pendingEnrichmentLabel || 'Pending Enrichment';
    if (status === 'enriching')          return labels.enrichingLabel || 'Enriching';
    if (status === 'contacted')          return labels.contactedDoneLabel || 'Contacted';
    if (status === 'passed')             return labels.passedLabel || 'Passed';
    if (status === 'filtered_out')       return labels.filteredOutLabel || 'Filtered Out';
    return labels.pendingEnrichmentLabel || 'Pending Enrichment';
}

// Status → button intent class. Pre-WI status mapping had only two states —
// actionable (`primary`) and terminal-or-missing (`muted`+disabled). The
// muted treatment made in-pipeline statuses (pending_enrichment / enriching)
// look broken because they read as failure rather than progress. The fix:
// reserve `muted` for terminal / confirmed-empty states only; in-pipeline
// statuses route to the new `inflight` intent (active outline, brand colour,
// no opacity dim). Authority: WI-LIVEDEALS-DEAL-CARD-ROOT-CAUSE-FIX-001 §F2.
function _statusIntent(status, canContact) {
    if (canContact) return 'primary';
    if (status === 'pending_enrichment' || status === 'enriching') return 'inflight';
    return 'muted';
}

// Per-founder outreach message resolver. Reads from the new
// outreach_messages_by_founder[] array first (matched by index, falling back
// to founder_full_name); falls back to the legacy outreach_template_linkedin
// for deals that pre-date the per-founder render (B1).
function _resolveOutreachMessage(deal, founder, index) {
    const messages = Array.isArray(deal && deal.outreach_messages_by_founder)
        ? deal.outreach_messages_by_founder
        : [];
    if (messages.length > 0) {
        const byIndex = messages.find((m) => m && m.founder_index === index);
        if (byIndex && byIndex.linkedin_message) return byIndex.linkedin_message;
        const byName = messages.find(
            (m) => m && founder && m.founder_full_name && m.founder_full_name === founder.full_name
        );
        if (byName && byName.linkedin_message) return byName.linkedin_message;
    }
    return (deal && deal.outreach_template_linkedin) || null;
}

// Compose Tailwind class strings from the INTENT_TO_BUTTON registry — the
// brand-canonical token source per DESIGN_LANGUAGE_SKILL.md / .impeccable.md
// Principle 5. Every interactive <Button> in this file routes through this
// helper instead of shadcn variant= so the discipline is enforceable at
// commit time (verify_intent_button_discipline.py).
function _intentClass(intent) {
    const tone = INTENT_TO_BUTTON[intent] || INTENT_TO_BUTTON.neutral;
    return `${tone.bg} ${tone.text} ${tone.border} ${tone.hover}`;
}

function _resolveDealId(deal) {
    if (!deal) return null;
    return deal._id || deal.deal_id || null;
}

function _buildDealMailtoHref(deal) {
    const founders = Array.isArray(deal.founders) ? deal.founders : [];
    const primary = founders.find((f) => f && f.email);
    if (!primary) return null;
    const ccs = founders
        .filter((f) => f && f.email && f.email !== primary.email)
        .map((f) => f.email);
    const subject = `${deal.company_name || 'Your company'} — quick intro`;
    const body = deal.outreach_template_email || '';
    // Build a base href using the canonical shared builder (encodeURIComponent,
    // not URLSearchParams.toString() — see mailtoBuilder.js for regression history).
    let href = buildMailtoHref(primary.email, subject, body);
    // Append cc separately — encodeURIComponent-encode the value so commas between
    // addresses are preserved while the param separator is a literal '&'.
    if (ccs.length) {
        href += `&cc=${encodeURIComponent(ccs.join(','))}`;
    }
    return href;
}

function _StatusChip({ label, intent = 'neutral', testId }) {
    const tone = INTENT_TO_BUTTON[intent] || INTENT_TO_BUTTON.neutral;
    return (
        <span
            className={`inline-flex items-center px-3 py-1 rounded-full border ${tone.text} ${tone.border}`}
            style={{
                ...DS_TYPOGRAPHY.badgeText,
                background: DS_COLORS.surface.warm,
            }}
            data-testid={testId}
        >
            {label}
        </span>
    );
}

// Leg 5 (WI-TRACXN-ENRICHMENT-BUILD-LEGS-3-7-001): the canonical sector_l3
// supersedes the legacy free-text sector_hint for the header sector line.
// sector_l3 null is an HONEST BLANK, not "no sector" — sector_l3_source says
// WHY, and each reason gets its own copy so a budget-cap pause (classifier_
// unavailable_honest_blank) never reads as "we looked and found nothing"
// (no_signal_honest_blank). A pre-Leg-4 record with no sector_l3_source at
// all falls back to the legacy sector_hint chip unchanged.
function _resolveSectorDisplay(deal, labels) {
    const l3 = deal.sector_l3;
    if (l3 && String(l3).trim().length > 0) {
        // The first look's tag is always PROVISIONAL (law §8.5): print the backend's own wording, never the bare code.
        const words = deal.sector_state && deal.sector_state.compact_label;
        return { label: words || labels.sectorNotCheckedCompactLabel || 'NOT CHECKED — not read on this surface', intent: 'info', testId: 'dealcard-sector-l3' };
    }
    const source = deal.sector_l3_source;
    switch (source) {
        case 'no_signal_honest_blank':
            return { label: labels.sectorNoSignalLabel || 'Sector — no confident match', intent: 'neutral', testId: 'dealcard-sector-no-signal' };
        case 'low_confidence_honest_blank':
            return { label: labels.sectorLowConfidenceLabel || 'Sector — signal too weak to confirm', intent: 'neutral', testId: 'dealcard-sector-low-confidence' };
        case 'classifier_unavailable_honest_blank':
            return { label: labels.sectorNotCheckedLabel || 'Sector — not checked yet', intent: 'neutral', testId: 'dealcard-sector-not-checked' };
        case 'taxonomy_violation_honest_blank':
            return { label: labels.sectorTagRejectedLabel || 'Sector — tag rejected, needs review', intent: 'warning', testId: 'dealcard-sector-rejected' };
        default:
            // No sector_l3_source at all — pre-Leg-4 record. Legacy fallback.
            return deal.sector_hint
                ? { label: deal.sector_hint, intent: 'info', testId: 'dealcard-sector-legacy-hint' }
                : null;
    }
}

// Leg 5: three explicit enrichment_resolution_state branches (ruled render,
// seat-test redteam). RESOLVED renders no extra chip — the sector chip above
// already shows real content. CONFIRMED_ABSENT and UNRESOLVED_AMBIGUOUS each
// get a distinct, honest chip: "Tracxn has nothing" (a real, confident
// answer) must never collapse into the same bucket as "we're not sure" — the
// fi.we case proved a single not-found bucket reads as a confident wrong
// answer. UNRESOLVED_AMBIGUOUS NEVER renders "new territory".
function _EnrichmentStatusChip({ deal, labels }) {
    const state = deal.enrichment_resolution_state;
    if (state === 'CONFIRMED_ABSENT') {
        return (
            <_StatusChip
                label={labels.enrichmentConfirmedAbsentLabel || 'Tracxn has nothing on this one'}
                intent="neutral"
                testId="dealcard-enrichment-confirmed-absent"
            />
        );
    }
    if (state === 'UNRESOLVED_AMBIGUOUS') {
        return (
            <_StatusChip
                label={labels.enrichmentAmbiguousLabel || 'Needs a human look'}
                intent="warning"
                testId="dealcard-enrichment-ambiguous"
            />
        );
    }
    return null;
}

// Leg 5: "Where we stand" — rendered ONLY when sector_l3 is known. An
// honest-blank sector_l3 means we do not know the sector, so we cannot know
// our stance in it either (ruled render). No live accrezeo_views read is
// wired in this leg — AS-3's one-road read funnel (TAXONOMY_SSOT_CONTRACT.md
// §2.3 / CHECK 3) requires any new reader to be declared in data_province
// .json's collection_registry.accrezeo_views.read_by; wiring that is a
// larger, separate change than this render leg. Per the dispatch's decision
// latitude: render honestly from data already on the deal payload, and never
// claim "new territory" merely because the read is missing — absence of a
// read is not absence of a view. So this line is deliberately an honest
// "not checked" admission, not a fabricated stance.
function _StanceSection({ deal, labels }) {
    const l3 = deal.sector_l3;
    if (!l3 || String(l3).trim().length === 0) return null;
    const template = labels.stanceNotCheckedTemplate || 'Where we stand in {{sector}} — not checked from this card yet.';
    // the bare code is never printed as if settled (law §8.5): the backend's own wording, or the not-checked words
    const sectorWords = (deal.sector_state && deal.sector_state.compact_label) || labels.sectorNotCheckedCompactLabel || 'NOT CHECKED — not read on this surface';
    return (
        <section className="mb-6" data-testid="dealcard-stance-section">
            <_SectionHeading>{labels.whereWeStandHeading || 'Where We Stand'}</_SectionHeading>
            <p style={{ ...DS_TYPOGRAPHY.bodyMd, color: DS_COLORS.text.muted, fontStyle: 'italic' }}>
                {template.replace('{{sector}}', sectorWords)}
            </p>
        </section>
    );
}

function _SectionHeading({ children }) {
    return (
        <h4
            className="mb-2"
            style={{
                ...DS_TYPOGRAPHY.sectionLabel || DS_TYPOGRAPHY.microLabel,
                color: DS_COLORS.text.secondary,
                textTransform: 'uppercase',
                letterSpacing: '0.08em',
            }}
        >
            {children}
        </h4>
    );
}

function _RAStatusSection({ deal }) {
    const synthesis = deal.researcher_synthesis;
    const completed = deal.ra_completed_at;
    const failureReason = deal.ra_failure_reason;
    const failedAt = deal.ra_failed_at;

    // Five-branch decision per WI-LIVEDEALS-DEAL-CARD-ROOT-CAUSE-FIX-001 §F3.
    // Every label comes from DS_LABELS so there are no hardcoded strings.
    let body;
    if (synthesis && String(synthesis).trim().length > 0) {
        body = (
            <p style={{ ...DS_TYPOGRAPHY.bodyMd, color: DS_COLORS.text.primary, whiteSpace: 'pre-wrap' }}>
                {synthesis}
            </p>
        );
    } else if (failureReason) {
        const template = DS_LABELS.researchFailureFallback || 'Research did not complete — reason: {{reason}}.';
        const text = template.replace('{{reason}}', String(failureReason));
        body = (
            <p
                style={{ ...DS_TYPOGRAPHY.bodyMd, color: DS_COLORS.text.muted, fontStyle: 'italic' }}
                data-testid="dealcard-ra-failure-reason"
            >
                {text}
            </p>
        );
    } else if (completed) {
        body = (
            <p style={{ ...DS_TYPOGRAPHY.bodyMd, color: DS_COLORS.text.muted, fontStyle: 'italic' }}>
                {DS_LABELS.researchEmptyLabel || 'Research ran — no public coverage found.'}
            </p>
        );
    } else if (failedAt) {
        body = (
            <p style={{ ...DS_TYPOGRAPHY.bodyMd, color: DS_COLORS.text.muted, fontStyle: 'italic' }}>
                {DS_LABELS.researchEmptyLabel || 'Research ran — no public coverage found.'}
            </p>
        );
    } else {
        body = (
            <p style={{ ...DS_TYPOGRAPHY.bodyMd, color: DS_COLORS.text.muted }}>
                {DS_LABELS.researchPendingLabel || 'Research pending.'}
            </p>
        );
    }
    return (
        <section className="mb-6" data-testid="dealcard-ra-section">
            <_SectionHeading>Research</_SectionHeading>
            {body}
        </section>
    );
}

// F4: Traction signal section — previously merged in _MERGE_FIELDS but
// never rendered. Renders the signal phrase when populated; renders a
// short empty-state line (DS_LABELS.tractionEmpty) when RA completed but
// found no traction; hidden entirely before RA runs.
function _TractionSection({ deal }) {
    const traction = deal.traction_signal;
    const completed = deal.ra_completed_at;
    if (!completed) return null;
    return (
        <section className="mb-6" data-testid="dealcard-traction-section">
            <_SectionHeading>Traction</_SectionHeading>
            {traction && String(traction).trim().length > 0 ? (
                <p style={{ ...DS_TYPOGRAPHY.bodyMd, color: DS_COLORS.text.primary }}>
                    {traction}
                </p>
            ) : (
                <p style={{ ...DS_TYPOGRAPHY.bodyMd, color: DS_COLORS.text.muted, fontStyle: 'italic' }}>
                    {DS_LABELS.tractionEmpty || 'No traction signal found in public research.'}
                </p>
            )}
        </section>
    );
}

function _FoundersSection({ deal, onOpenFounder }) {
    const founders = Array.isArray(deal.founders) ? deal.founders : [];
    const fromResearch = Array.isArray(deal.founders_from_research) ? deal.founders_from_research : [];
    const fallbackName = deal.founder_name_from_research;

    // Nothing to render in any of the three modes.
    if (founders.length === 0 && fromResearch.length === 0 && !fallbackName) return null;

    return (
        <section className="mb-6" data-testid="dealcard-founders-section">
            <_SectionHeading>Founders</_SectionHeading>
            {founders.length > 0 ? (
                <div className="space-y-2">
                    {founders.slice(0, 3).map((f, i) => (
                        <div
                            key={f.email || f.linkedin_url || `${f.full_name || ''}-${i}`}
                            className="flex items-center justify-between"
                        >
                            <div>
                                <p style={{ ...DS_TYPOGRAPHY.bodyMd, fontWeight: 600, color: DS_COLORS.text.primary }}>
                                    {f.full_name || 'Unknown'}
                                </p>
                                {f.designation && (
                                    <p style={{ ...DS_TYPOGRAPHY.sectionSubLabel, color: DS_COLORS.text.muted }}>
                                        {f.designation}
                                    </p>
                                )}
                            </div>
                            {f.linkedin_url && (
                                // PKG-076-EXEMPT: 44px enforcement via DS_SPACING token until
                                // Button.css base-rule min-height lands (descoped F1 in WI-LIVEDEALS-
                                // DEAL-CARD-ROOT-CAUSE-FIX-001 due to CSS-variable conflict). Tracked
                                // as follow-up; verifier-aware exemption documents the open issue.
                                <Button
                                    type="button"
                                    className={_intentClass('neutral')}
                                    size="sm"
                                    onClick={() => onOpenFounder({ founder: f, index: i })}
                                    aria-label={`${DS_LABELS.openFounderLinkedinAriaPrefix || 'Open'} ${f.full_name} ${DS_LABELS.linkedinDetailsAriaSuffix || 'LinkedIn details'}`}
                                    style={{ minHeight: DS_SPACING.minTouchTarget }}
                                >
                                    <LinkedinLogo size={16} weight="bold" />
                                </Button>
                            )}
                        </div>
                    ))}
                </div>
            ) : fromResearch.length > 0 ? (
                // F4: RA-discovered founders array (Perplexity output). Surfaced as a
                // proper list rather than the legacy single-name fallback, so the CEO
                // sees every founder + LinkedIn URL RA found even before Cowork enriches.
                <div className="space-y-2" data-testid="dealcard-ra-founders-array">
                    {fromResearch.slice(0, 3).map((f, i) => (
                        <div
                            key={f.linkedin_url || `${f.name || ''}-${i}`}
                            className="flex items-center justify-between"
                        >
                            <div>
                                <p style={{ ...DS_TYPOGRAPHY.bodyMd, fontWeight: 600, color: DS_COLORS.text.primary }}>
                                    {f.name || 'Unknown'}
                                </p>
                                <p
                                    style={{
                                        ...DS_TYPOGRAPHY.sectionSubLabel,
                                        color: DS_COLORS.text.muted,
                                        fontStyle: 'italic',
                                    }}
                                >
                                    from public research — email not yet confirmed
                                </p>
                            </div>
                            {f.linkedin_url && (
                                <a
                                    href={f.linkedin_url}
                                    target="_blank"
                                    rel="noopener noreferrer"
                                    aria-label={`${DS_LABELS.openFounderLinkedinAriaPrefix || 'Open'} ${f.name || ''} ${DS_LABELS.linkedinDetailsAriaSuffix || 'LinkedIn details'}`}
                                    style={{ color: DS_COLORS.text.primary }}
                                >
                                    <LinkedinLogo size={16} weight="bold" />
                                </a>
                            )}
                        </div>
                    ))}
                </div>
            ) : (
                <div data-testid="dealcard-ra-founder-fallback">
                    <p style={{ ...DS_TYPOGRAPHY.bodyMd, fontWeight: 600, color: DS_COLORS.text.primary }}>
                        {fallbackName}
                    </p>
                    <p
                        style={{
                            ...DS_TYPOGRAPHY.sectionSubLabel,
                            color: DS_COLORS.text.muted,
                            fontStyle: 'italic',
                        }}
                    >
                        from public research — email not yet confirmed
                    </p>
                </div>
            )}
        </section>
    );
}

// FL-4 (WI-TA-FIRST-LOOK-FL4-BUILD-THE-CARD-2026-10-14): the first look. The backend composes it
// (backend/core/orchestration/first_look_card.py) as the contract's nine sections, each line either a
// fact the record holds (known) or an explicit unknown. This component only DISPLAYS it: it infers and
// fills nothing, so a missing fact stays visibly missing. A deal without `first_look` is an old-format
// card and never reaches this component.
function _FirstLookLine({ line, testIdPrefix }) {
    return (
        <p
            className="mb-1"
            data-testid={line.known ? `${testIdPrefix}-known` : `${testIdPrefix}-unknown`}
            style={
                line.known
                    ? { ...DS_TYPOGRAPHY.bodyMd, color: DS_COLORS.text.primary }
                    : { ...DS_TYPOGRAPHY.bodyMd, color: DS_COLORS.text.muted, fontStyle: 'italic' }
            }
        >
            {line.text}
        </p>
    );
}

// R04 (19 Oct 2026): the first look of a COMPANY (any source), composed by `compose_company_first_look` from the company's saved
// first look. The CEO reads a short synthesis and the recommendation (stated as owed while none is written) and can open ONE
// disclosure for what it rests on. Still display-only: nothing is inferred or filled here.
function _CompanyFirstLook({ firstLook }) {
    const synthesis = Array.isArray(firstLook.synthesis) ? firstLook.synthesis : [];
    const recommendation = firstLook.recommendation || null;
    const evidence = firstLook.evidence || null;
    return (
        <div data-testid="dealcard-first-look" data-first-look-version="company">
            <section className="mb-6" data-testid="dealcard-first-look-synthesis">
                <_SectionHeading>In short</_SectionHeading>
                {synthesis.map((line, i) => (
                    <_FirstLookLine key={`syn-${i}`} line={line} testIdPrefix="dealcard-first-look" />
                ))}
            </section>
            {recommendation && (
                <section className="mb-6" data-testid="dealcard-first-look-recommendation">
                    <_SectionHeading>{recommendation.heading}</_SectionHeading>
                    {(recommendation.lines || []).map((line, i) => (
                        <_FirstLookLine key={`rec-${i}`} line={line} testIdPrefix="dealcard-first-look" />
                    ))}
                </section>
            )}
            {evidence && Array.isArray(evidence.lines) && evidence.lines.length > 0 && (
                <details className="mb-6" data-testid="dealcard-first-look-evidence">
                    <summary
                        style={{
                            ...DS_TYPOGRAPHY.bodyMd,
                            color: DS_COLORS.text.primary,
                            fontWeight: 700,
                            minHeight: DS_SPACING.minTouchTarget,
                            display: 'flex',
                            alignItems: 'center',
                            cursor: 'pointer',
                        }}
                    >
                        {evidence.heading}
                    </summary>
                    <div className="mt-2">
                        {evidence.lines.map((line, i) => (
                            <_FirstLookLine key={`ev-${i}`} line={line} testIdPrefix="dealcard-first-look-evidence-line" />
                        ))}
                    </div>
                </details>
            )}
        </div>
    );
}

// A first look that could not be prepared SAYS so. It never shows an older, finished-looking card in its place.
function _FirstLookUnavailable({ failure }) {
    const reason = (failure && failure.reason) || 'the reason was not recorded';
    return (
        <section className="mb-6" data-testid="dealcard-first-look-unavailable">
            <_SectionHeading>The first look could not be prepared</_SectionHeading>
            <p style={{ ...DS_TYPOGRAPHY.bodyMd, color: DS_COLORS.text.primary }}>
                {`Nothing is shown in its place: ${reason}. It is prepared again when the company is next researched.`}
            </p>
        </section>
    );
}

function _FirstLookSection({ firstLook }) {
    if (firstLook && Array.isArray(firstLook.synthesis)) return <_CompanyFirstLook firstLook={firstLook} />;
    const sections = Array.isArray(firstLook && firstLook.sections) ? firstLook.sections : [];
    return (
        <div data-testid="dealcard-first-look">
            {sections.map((section) => (
                <section key={section.id} className="mb-6" data-testid={`dealcard-first-look-${section.id}`}>
                    <_SectionHeading>{section.heading}</_SectionHeading>
                    {(section.lines || []).map((line, i) => (
                        <_FirstLookLine key={`${section.id}-${i}`} line={line} testIdPrefix="dealcard-first-look" />
                    ))}
                </section>
            ))}
        </div>
    );
}

function _ActionBar({ deal, canContact, onPassClick, onContactedClick, onCopyDeck, deckCopied, contacting, passing }) {
    const dealId = _resolveDealId(deal);
    const mailtoHref = _buildDealMailtoHref(deal);
    const [showPassDropdown, setShowPassDropdown] = useState(false);
    const status = deal && deal.status;

    const passLabel = passing ? PASS_ACTION.loadingLabel || 'Passing...' : PASS_ACTION.label || 'Pass';
    const contactedLabel = canContact
        ? CONTACTED_ACTION.label || DS_LABELS.contactedLabel || 'Mark as Contacted'
        : _statusLabel(status, DS_LABELS);

    const handleSelectReason = (reasonId) => {
        setShowPassDropdown(false);
        onPassClick(reasonId);
    };

    // Raw <button> elements — NOT the shared <Button> wrapper. The wrapper
    // unconditionally prepends `btn btn-primary` (Button.jsx line 26) and the
    // `.btn-primary` rule in Button.css applies brand purple via CSS variable,
    // overriding any Tailwind bg-* token passed via className. Routing through
    // INTENT_TO_BUTTON is only effective when the raw element is rendered.
    // Resolving the CSS-variable conflict in Button.jsx / Button.css is tracked
    // as a follow-up WI; see WI-LIVEDEALS-DEAL-CARD-ROOT-CAUSE-FIX-001 §F1
    // descope rationale. All raw <button> elements + inline styles below carry
    // PKG-076-EXEMPT comments documenting this open structural debt.
    const baseBtnClass =
        'px-4 py-2 rounded-md font-semibold text-sm transition-colors border inline-flex items-center gap-2 disabled:opacity-60 disabled:cursor-not-allowed';
    const btnStyle = { minHeight: DS_SPACING.minTouchTarget, fontFamily: 'DM Sans, system-ui, sans-serif' };
    const contactedIntent = _statusIntent(status, canContact);

    return (
        <div className="flex flex-wrap gap-2" data-testid="dealcard-action-bar">
            {/* Send Email — mailto, no backend call. Intent: neutral (mailto does not commit data). */}
            {mailtoHref ? (
                <a href={mailtoHref} target="_blank" rel="noopener noreferrer">
                    {/* PKG-076-EXEMPT: raw <button> + inline btnStyle — same Button.jsx CSS-variable conflict as the rest of _ActionBar. Migrating to <Button> requires resolving the .btn-primary override first. */}
                    <button type="button" className={`${baseBtnClass} ${_intentClass('neutral')}`} style={btnStyle}>
                        <EnvelopeSimple size={16} weight="bold" />
                        {DS_LABELS.sendEmailLabel || 'Send Email'}
                    </button>
                </a>
            ) : (
                /* PKG-076-EXEMPT: see _ActionBar header comment. Tooltip explains the muted/disabled state so the dimmed button reads as a state, not a bug. */
                <button
                    type="button"
                    disabled
                    className={`${baseBtnClass} ${_intentClass('muted')}`}
                    style={btnStyle}
                    title={DS_LABELS.noContactTooltip || ''}
                    aria-label={DS_LABELS.noContactTooltip || DS_LABELS.noContactFoundLabel}
                >
                    <XCircle size={16} weight="bold" />
                    {DS_LABELS.noContactFoundLabel || 'No Contact Found'}
                </button>
            )}

            {/* Pass with dropdown — intent: negative (Pass is destructive for the deal). */}
            <DropdownMenu open={showPassDropdown} onOpenChange={setShowPassDropdown}>
                <DropdownMenuTrigger asChild>
                    {/* PKG-076-EXEMPT: see _ActionBar header comment. */}
                    <button
                        type="button"
                        disabled={passing || !dealId}
                        aria-label={DS_LABELS.passChooseReasonAriaLabel}
                        data-testid="dealcard-pass-trigger"
                        className={`${baseBtnClass} ${_intentClass('negative')}`}
                        style={btnStyle}
                    >
                        {passLabel}
                        <CaretDown size={14} />
                    </button>
                </DropdownMenuTrigger>
                <DropdownMenuContent align="end">
                    {PASS_REASONS.map((reason) => (
                        <DropdownMenuItem
                            key={reason.id}
                            onSelect={() => handleSelectReason(reason.id)}
                            data-testid={`dealcard-pass-reason-${reason.id}`}
                        >
                            {reason.label}
                        </DropdownMenuItem>
                    ))}
                </DropdownMenuContent>
            </DropdownMenu>

            {/* Mark as Contacted — primary when actionable, inflight while pipeline runs, muted on terminal states. */}
            {/* PKG-076-EXEMPT: see _ActionBar header comment. */}
            <button
                type="button"
                disabled={!canContact || contacting || !dealId}
                onClick={onContactedClick}
                data-testid="dealcard-contacted-button"
                data-deal-status={status || ''}
                className={`${baseBtnClass} ${_intentClass(contactedIntent)}`}
                style={btnStyle}
                title={contactedIntent === 'inflight' ? (DS_LABELS.inflightTooltip || '') : ''}
            >
                {contacting ? CONTACTED_ACTION.loadingLabel || 'Updating...' : contactedLabel}
            </button>

            {/* View Deck — both surfaces (WI-INVARIANT-UI-SURFACE-REPAIR-001,
                PKG-009). Previously inbox-only; the Deal Sourcing agent page
                registers view_deck as a top-level action, so it must render on
                surface="agents" too. Client-side clipboard copy (handleCopyDeck),
                no backend — mirrors the Send Email mailto precedent. Intent:
                neutral; muted with tooltip when no deck path is present. */}
            {deal.deck_local_path ? (
                /* PKG-076-EXEMPT: see _ActionBar header comment. */
                <button
                    type="button"
                    onClick={onCopyDeck}
                    aria-label={DS_LABELS.copyDeckAriaLabel}
                    data-testid="dealcard-view-deck"
                    className={`${baseBtnClass} ${_intentClass('neutral')}`}
                    style={btnStyle}
                >
                    {deckCopied ? <Check size={16} weight="bold" /> : <Eye size={16} weight="bold" />}
                    <span>
                        {deckCopied ? DS_LABELS.viewDeckCopiedLabel || 'Copied' : VIEW_DECK_ACTION.label || 'View Deck'}
                    </span>
                </button>
            ) : (
                /* PKG-076-EXEMPT: see _ActionBar header comment. */
                <button
                    type="button"
                    disabled
                    className={`${baseBtnClass} ${_intentClass('muted')}`}
                    style={btnStyle}
                    title={DS_LABELS.noDeckTooltip || ''}
                    aria-label={DS_LABELS.noDeckTooltip || DS_LABELS.noDeckAvailableLabel}
                >
                    {DS_LABELS.noDeckAvailableLabel || 'No Deck Available'}
                </button>
            )}
        </div>
    );
}

/**
 * DealCard
 *
 * @param {object}   deal              live or snapshot deal record (one shape; adapters normalise)
 * @param {string}   surface           "inbox" | "agents"
 * @param {function} onPass            (dealId, reason?) => Promise<void> — MUST reject/throw on failure so the aria-live announcer can tell success from failure (Leg 5)
 * @param {function} onContacted       (dealId) => Promise<void> — same contract as onPass
 * @param {function} onRefresh         () => void  — called after successful contact
 * @param {function} [onAnnounce]      (message: string) => void — optional relay to a PARENT-owned, persistent aria-live region. DealCard also renders its own local live region so error/loading announcements always work even when unwired; a parent hook is only needed to survive this row unmounting on a successful Pass/Contacted (the row leaves the list on success, so a locally-owned live region node can disappear in the same commit as the text change — see Leg 5 report).
 * @param {boolean}  [isLoading=false] — render loading state when true
 * @param {string}   [className]
 */
export function DealCard({
    deal,
    surface = 'inbox',
    onPass,
    onContacted,
    onRefresh,
    onAnnounce,
    isLoading = false,
    className = '',
}) {
    const [passing, setPassing] = useState(false);
    const [contacting, setContacting] = useState(false);
    const [deckCopied, setDeckCopied] = useState(false);
    const [founderPanelData, setFounderPanelData] = useState(null);
    // Leg 5 (a11y): local live-region text — always rendered so Pass /
    // Contacted / View Deck outcomes are perceivable by a screen reader, not
    // just visible. Mirrors the #950/#954 pattern in LinkedInPostAgent.jsx.
    // WI-POST-BUTTON-REGRESSION-HUNT-001 — via useAnnouncer, not a bare setState:
    // a repeated identical outcome is a React no-op and would be spoken once and
    // never again (the Post Studio failure class). See the hook for the full why.
    const [statusMessage, announceLocal] = useAnnouncer();

    const dealId = _resolveDealId(deal);
    const status = deal && deal.status;
    const canContact = status === DEAL_CONTACTABLE_STATUS;
    const companyName = (deal && deal.company_name) || 'this deal';

    const announce = useCallback(
        (msg) => {
            announceLocal(msg);
            if (typeof onAnnounce === 'function') onAnnounce(msg);
        },
        [onAnnounce, announceLocal]
    );

    const handlePass = useCallback(
        async (reason) => {
            if (!dealId || !onPass) return;
            try {
                setPassing(true);
                await onPass(dealId, reason || null);
                announce((DS_LABELS.passAnnounceSuccess || '{{company}} passed.').replace('{{company}}', companyName));
            } catch {
                // No silent masking: a screen reader must hear the failure, not
                // just the visual toast a parent handler may already show.
                announce((DS_LABELS.passAnnounceError || 'Could not pass {{company}} — please try again.').replace('{{company}}', companyName));
            } finally {
                setPassing(false);
            }
        },
        [dealId, onPass, announce, companyName]
    );

    const handleContacted = useCallback(async () => {
        if (!dealId || !onContacted || !canContact) return;
        try {
            setContacting(true);
            await onContacted(dealId);
            onRefresh && onRefresh();
            announce((DS_LABELS.contactedAnnounceSuccess || '{{company}} marked as contacted.').replace('{{company}}', companyName));
        } catch {
            announce((DS_LABELS.contactedAnnounceError || 'Could not mark {{company}} as contacted — please try again.').replace('{{company}}', companyName));
        } finally {
            setContacting(false);
        }
    }, [dealId, onContacted, onRefresh, canContact, announce, companyName]);

    const handleCopyDeck = useCallback(async () => {
        const path = deal && deal.deck_local_path;
        if (!path) return;
        let copied = false;
        try {
            await navigator.clipboard.writeText(path);
            copied = true;
        } catch {
            // Best-effort fallback for browsers without clipboard API
            const ta = document.createElement('textarea');
            ta.value = path;
            document.body.appendChild(ta);
            ta.select();
            try { document.execCommand('copy'); copied = true; } catch { /* ignore */ }
            document.body.removeChild(ta);
        }
        setDeckCopied(copied);
        announce(copied
            ? (DS_LABELS.viewDeckCopiedAnnounce || 'Deck path copied to clipboard.')
            : (DS_LABELS.viewDeckCopyErrorAnnounce || 'Could not copy the deck path — please try again.'));
        if (copied) setTimeout(() => setDeckCopied(false), 2000);
    }, [deal, announce]);

    if (isLoading) {
        return (
            <div
                className={`bg-white rounded-xl border border-slate-200 p-6 ${className}`}
                data-testid="dealcard-loading"
            >
                <div
                    className="h-6 w-48 mb-4 rounded animate-pulse"
                    style={{ background: DS_COLORS.surface.warm }}
                />
                <div
                    className="h-4 w-full mb-2 rounded animate-pulse"
                    style={{ background: DS_COLORS.surface.warm }}
                />
                <div
                    className="h-4 w-2/3 rounded animate-pulse"
                    style={{ background: DS_COLORS.surface.warm }}
                />
            </div>
        );
    }

    if (!deal) return null;

    // formatRaise destructures { usd, inr } (lib/formatRaise.js:19); passing the
    // raise_amount_* keys directly leaves both fields undefined and the chip
    // never renders. Mirror the working call in DealListRow.jsx:100.
    const raiseLabel = formatRaise({
        usd: deal.raise_amount_usd,
        inr: deal.raise_amount_inr,
    });
    const roundLabel = raiseLabel || deal.round_type || '';
    // FL-4: a card that carries a first look is a new-format card; one without it renders exactly as before.
    const firstLook = deal.first_look && (Array.isArray(deal.first_look.sections) || Array.isArray(deal.first_look.synthesis))
        ? deal.first_look
        : null;
    // A company card whose first look could not be composed is still a new-format card: it says so, and never falls back to the old layout.
    const firstLookFailed = !firstLook && deal.first_look_source === 'company';
    let sectorDisplay = _resolveSectorDisplay(deal, DS_LABELS);
    // The contract never substitutes Tracxn's own free-text sector for the Accrezeo tag on a first look.
    if ((firstLook || firstLookFailed) && sectorDisplay && sectorDisplay.testId === 'dealcard-sector-legacy-hint') sectorDisplay = null;

    return (
        <article
            className={`bg-white rounded-xl border border-slate-200 p-6 ${className}`}
            data-surface={surface}
            data-testid="dealcard-root"
        >
            {/* Leg 5 (a11y): polite live region — announces Pass / Contacted /
                View Deck outcomes (success AND failure) to a screen reader.
                Visually hidden; the visible button label + toast stay for
                sighted users. See the DealCard onAnnounce JSDoc above for the
                unmount-on-success caveat. */}
            <div role="status" aria-live="polite" className="sr-only">{statusMessage}</div>

            {/* Header — flat single-row for inbox; agents-page list shells handle collapsible chrome at the row level */}
            <header className="mb-4">
                <h3
                    style={{
                        ...DS_TYPOGRAPHY.listItemTitle,
                        color: DS_COLORS.text.primary,
                        textTransform: 'none',
                        margin: 0,
                    }}
                    data-testid="dealcard-title"
                >
                    {deal.company_name || 'Untitled deal'}
                </h3>
                <div className="mt-2 flex flex-wrap gap-2">
                    {roundLabel && <_StatusChip label={roundLabel} intent="neutral" />}
                    {sectorDisplay && (
                        <_StatusChip label={sectorDisplay.label} intent={sectorDisplay.intent} testId={sectorDisplay.testId} />
                    )}
                    {deal.arr_signal && (
                        <_StatusChip
                            label={DS_LABELS.arrSignalLabel}
                            intent="positive"
                        />
                    )}
                    <_EnrichmentStatusChip deal={deal} labels={DS_LABELS} />
                </div>
            </header>

            {firstLook
                ? <_FirstLookSection firstLook={firstLook} />
                : firstLookFailed
                    ? <_FirstLookUnavailable failure={deal.first_look_failure} />
                    : <_StanceSection deal={deal} labels={DS_LABELS} />}

            {deal.live_data_missing && (
                <p
                    className="mb-3"
                    style={{ ...DS_TYPOGRAPHY.sectionSubLabel, color: DS_COLORS.text.muted }}
                    data-testid="dealcard-live-data-missing-meta"
                >
                    {/* Silent-fallback marker — surface for tests / debugging only; CEO direction: no visible indicator. */}
                </p>
            )}

            {!firstLook && !firstLookFailed && deal.description && (
                <section className="mb-6" data-testid="dealcard-description-section">
                    <_SectionHeading>About</_SectionHeading>
                    <p style={{ ...DS_TYPOGRAPHY.bodyMd, color: DS_COLORS.text.primary }}>
                        {deal.description}
                    </p>
                </section>
            )}

            {!firstLook && !firstLookFailed && <_RAStatusSection deal={deal} />}

            {!firstLook && !firstLookFailed && <_TractionSection deal={deal} />}

            <_FoundersSection deal={deal} onOpenFounder={setFounderPanelData} />

            {/* F6: three-branch render so the CEO can distinguish "no investors" from
                "RA never ran". When ra_completed_at is null we hide the section
                entirely (the RA section already tells the CEO research is pending). */}
            {(firstLook || firstLookFailed) ? null : deal.existing_investors ? (
                <section className="mb-6" data-testid="dealcard-investors-section">
                    <_SectionHeading>Existing Investors</_SectionHeading>
                    <p style={{ ...DS_TYPOGRAPHY.bodyMd, color: DS_COLORS.text.primary }}>
                        {deal.existing_investors}
                    </p>
                </section>
            ) : deal.ra_completed_at ? (
                <section className="mb-6" data-testid="dealcard-investors-section">
                    <_SectionHeading>Existing Investors</_SectionHeading>
                    <p style={{ ...DS_TYPOGRAPHY.bodyMd, color: DS_COLORS.text.muted, fontStyle: 'italic' }}>
                        {DS_LABELS.investorsEmpty || 'No public investor data found.'}
                    </p>
                </section>
            ) : null}

            <_ActionBar
                deal={deal}
                canContact={canContact}
                passing={passing}
                contacting={contacting}
                deckCopied={deckCopied}
                onPassClick={handlePass}
                onContactedClick={handleContacted}
                onCopyDeck={handleCopyDeck}
            />

            {founderPanelData && (
                <FounderDetailPanel
                    founder={founderPanelData.founder}
                    outreachMessage={_resolveOutreachMessage(deal, founderPanelData.founder, founderPanelData.index)}
                    labels={DS_LABELS}
                    onClose={() => setFounderPanelData(null)}
                />
            )}
        </article>
    );
}

// The display pieces the company read after contact (Startups page, R06) reuses, so the CEO sees the first look in the same words
// and the same unknown/known styling after the card has left his inbox. Display only.
export { _CompanyFirstLook as CompanyFirstLook, _FirstLookLine as FirstLookLine, _SectionHeading as FirstLookHeading };

export default DealCard;
